import { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';

import Utils from "../../../utils/utils";
import Forms from "../../../services/forms";
import LeftMenu from "./controls/LeftMenu";
import FormTemplateEditor from "./templates/FormTemplateEditor";
import FormTemplateList from "./templates/FormTemplateList";
import SharedSectionsList from "./sharedSections/SharedSectionsList";
import SharedSection from "./sharedSections/SharedSection";
import FormInstancesDetails from "./FormInstancesDetails";
import CreateUser from "../../user/CreateUser";
import UserList from "../../user/UserList";
import UserDetails from "../../user/UserDetails";
import FormPreferences from "./FormPreferences";
import { useHasPermission } from "../../../common/Hooks";
import PubSub from "../../../services/PubSub";
import { FormsConfigProvider } from "./Context";
import DataListsEditor from "./DataListsEditor";
import { FormsClipboardProvider } from "./Clipboard";
import Deployment from "../../../services/deployment";
import DeploymentSettings from "./DeploymentSettings";
import { SharedSectionProvider } from "./SharedSectionContext";
import { useRoles } from "../../../common/context/Roles";

const FormsManagement = () => {
    const history = useHistory();
    const { objectType, id: deploymentId, objectSubType, objectSubTypeId, objectSubTypeVersion } = useParams();
    const formTemplateName = objectSubType === 'forms-templates' ? objectSubTypeId : null;
    const formTemplateVersion = objectSubType === 'forms-templates' ? objectSubTypeVersion : null;
    const sharedSectionId = objectSubType === 'shared-sections' ? objectSubTypeId : null;

    const [showCreateUserModal, setShowCreateUserModal] = useState(false);

    const [translate] = useTranslation();
    const deploymentPermissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "deployment"
    });
    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "forms"
    });
    const completerPermissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "formsCompleter"
    });
    const roles = useRoles();

    const configList = useMemo(() => {
        const arr = [];

        if (roles.includes("forms-admin") || roles.includes("app-admin")) {
            arr.push({ _id: "users", displayValue: translate('OTM_USERS') });
            arr.push({ _id: "forms-instances", displayValue: translate('OTM_FORM_INSTANCES') });
            arr.push({ _id: "forms-templates", displayValue: translate('OTM_TEMPLATES') });
            arr.push({ _id: "shared-sections", displayValue: translate('OTM_FORMS_SHARED_SECTIONS') });
            arr.push({ _id: "data-lists", displayValue: translate('OTM_DATA_LISTS') });
            arr.push({ _id: "preferences", displayValue: translate('OTM_FORMS_TEMPLATE_PREFERENCES') });
            arr.push({ _id: "deployment-settings", displayValue: translate('OTM_FORMS_TEMPLATE_DEPLOYMENT_SETTINGS') });
        }
        else if (roles.includes("forms-manager")) {
            arr.push({ _id: "users", displayValue: translate('OTM_USERS') });
            arr.push({ _id: "forms-instances", displayValue: translate('OTM_FORM_INSTANCES') });
            arr.push({ _id: "forms-templates", displayValue: translate('OTM_TEMPLATES') });
        }
        else if (roles.includes("forms-configurator")) {
            arr.push({ _id: "forms-templates", displayValue: translate('OTM_TEMPLATES') });
            arr.push({ _id: "shared-sections", displayValue: translate('OTM_FORMS_SHARED_SECTIONS') });
            arr.push({ _id: "data-lists", displayValue: translate('OTM_DATA_LISTS') });
            arr.push({ _id: "preferences", displayValue: translate('OTM_FORMS_TEMPLATE_PREFERENCES') });
            arr.push({ _id: "deployment-settings", displayValue: translate('OTM_FORMS_TEMPLATE_DEPLOYMENT_SETTINGS') });
        }

        return arr;
    }, [roles, translate]);

    useEffect(() => {
        if (!objectSubType && configList.length > 0 && (roles.length > 1 || !roles.includes("forms-completer"))) {
            history.push(`/${objectType}/${deploymentId}/forms-templates/`);
        }
    }, [objectSubType, configList, objectType, deploymentId, roles, history]);

    const [formConfig, setFormConfig] = useState();
    const [formDataLists, setFormDataLists] = useState();
    const [formsPreferences, setFormsPreferences] = useState({});
    const [deploymentUrl, setDeploymentUrl] = useState(null);
    const [deployment, setDeployment] = useState({});

    const getFormConfig = useCallback(async () => {
        const configResponse = await Forms.getFormConfig({
            deploymentId
        });
        if (!configResponse.error) {
            setFormConfig(configResponse.data);
        }
    }, [deploymentId]);

    const getFormDataLists = useCallback(async () => {
        const dataListsResponse = await Forms.getDataLists({
            deploymentId
        });
        if (!dataListsResponse.error) {
            setFormDataLists(dataListsResponse.data);
        }
    }, [deploymentId]);

    const getDeployment = useCallback(async (id) => {
        const response = await Deployment.getDeployment(id);
        if (!response.error) {
            const url = response.data.cname ? response.data.cname : response.data.otmApp.cloudfrontUrl;

            setDeployment(response.data);
            setDeploymentUrl(url ? `https://${url}${response.data.homePage}` : null);
            setFormsPreferences(response.data.formsPreferences);
        }
        else {
            setDeploymentUrl(null);
            setFormsPreferences({});
        }
    }, []);

    useEffect(() => {
        if (permissions.read && !formConfig) {
            getFormConfig();
        }
    }, [permissions, formConfig, getFormConfig]);

    useEffect(() => {
        if (permissions.read && !formDataLists) {
            getFormDataLists();
        }
    }, [permissions, formDataLists, getFormDataLists]);

    useEffect(() => {
        const subscriptionId = PubSub.subscribe("refresh-data-lists", () => {
            getFormDataLists();
        });

        return () => PubSub.unsubscribe("refresh-data-lists", subscriptionId);
    }, [getFormDataLists]);

    useEffect(() => {
        const subscriptionId = PubSub.subscribe("refresh-forms-preferences", () => {
            getDeployment(deploymentId);
        });

        return () => PubSub.unsubscribe("refresh-forms-preferences", subscriptionId);
    }, [getDeployment, deploymentId]);

    useEffect(() => {
        if (deploymentPermissions.read && deploymentId) {
            getDeployment(deploymentId)
        }
    }, [getDeployment, configList, completerPermissions, deploymentPermissions, deploymentId]);

    const renderContent = useMemo(() => {
        switch (objectSubType) {
            case 'forms-templates': {
                if (formTemplateName && formTemplateVersion) {
                    return <FormTemplateEditor />
                }
                return <FormTemplateList />
            }
            case 'shared-sections': {
                return (
                    <SharedSectionProvider>
                        {sharedSectionId ? <SharedSection /> : <SharedSectionsList />}
                    </SharedSectionProvider>
                );
            }
            case 'data-lists': {
                return <DataListsEditor />
            }
            case 'users': {
                return (
                    <div className="row w-100">
                        <div className="col-4">
                            <CreateUser show={showCreateUserModal} onCancel={() => setShowCreateUserModal(false)} />
                            <UserList basePath={`/forms-config/${deploymentId}/users`} onClickNew={() => setShowCreateUserModal(true)} />
                        </div>
                        <div className="col-8 mt-n3">
                            <UserDetails userIdLoc="objectSubTypeId" deploymentIdLoc="id" showRolesOnly={true} />
                        </div>
                    </div>
                );
            }
            case 'forms-instances': {
                return <FormInstancesDetails />
            }
            case 'preferences': {
                return <FormPreferences />
            }
            case 'deployment-settings': {
                return <DeploymentSettings
                    deployment={deployment}
                    deploymentUrl={deploymentUrl} />
            }
            default: {
                return <></>
            }
        }
    }, [deploymentId, deployment, deploymentUrl, objectSubType, formTemplateName, formTemplateVersion, sharedSectionId, showCreateUserModal]);

    if (configList && configList.length === 0 && completerPermissions.update) {
        return <div className="py-3 ps-lg-3 text-center d-flex flex-column">
            <span className="h4">
                {translate("OTM_FORMS_COMPLETER_MESSAGE")}
            </span>
            <span className="h5">
                <a href={deploymentUrl} rel='noreferrer' target='_blank'>
                    {deploymentUrl}
                </a>
            </span>
        </div>
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <FormsClipboardProvider>
                <div className="row g-0 flex-lg-grow-1 overflow-y-lg-hidden">
                    <LeftMenu
                        listData={configList}
                        listAttr={"displayValue"}
                        listOnClick={(_id) => { history.push(`/${objectType}/${deploymentId}/${_id}/`); }}
                        selectedTab={objectSubType}
                        showClipboard={["forms-templates", "shared-sections"].includes(objectSubType) && permissions.update}
                        disabled={!permissions.read}
                    />
                    <div className="col-12 col-lg-10 h-lg-100 overflow-y-lg-hidden">
                        <div className="h-lg-100 d-lg-flex flex-lg-column overflow-y-lg-hidden py-3 ps-lg-3">
                            <FormsConfigProvider formConfig={formConfig} formDataLists={formDataLists} formsPreferences={formsPreferences}>
                                {renderContent}
                            </FormsConfigProvider>
                        </div>
                    </div>
                </div>
            </FormsClipboardProvider>
        </DndProvider>
    );
}

export default FormsManagement;